import { useLingui } from '@lingui/react/macro';
import { TriangleAlert } from 'lucide-react';
import React, { useCallback } from 'react';
import UDButton from '../button';
import UDText from '../text';
import UDModal, { UDModalSize } from './modal';
import { ModalFooter, ModalHeader } from './modal-parts';

/**
 * Тон окна подтверждения. Критерий — необратимость по тексту самого окна, а не слово «Удалить»:
 * - `danger` — после действия данные не вернуть (удалить, исключить, перезаписать, уйти с потерей правок);
 * - `neutral` — ничего не теряется (сохранить, отключить то, что можно подключить снова).
 */
export type UDConfirmModalTone = 'danger' | 'neutral';

export interface UDConfirmModalProps {
  show: boolean;

  /** Вопрос по канону: «Удалить команду «Метеор»?» */
  title: string;
  /** Пояснение под заголовком. Обычно «Это действие необратимо.» */
  description?: string;

  /** Подпись кнопки действия — глагол: «Удалить», «Исключить», «Покинуть». */
  confirmText?: string;
  cancelText?: string;

  /** `danger` (по умолчанию) — красная кнопка действия и пояснение блоком предупреждения; `neutral` — жёлтая кнопка и серый подзаголовок. */
  tone?: UDConfirmModalTone;

  isLoading?: boolean;

  onConfirm: () => void | Promise<unknown>;
  /** Закрытие окна: и кнопкой отмены, и крестиком, и Escape, и кликом по затемнению. */
  onCancel: () => void;

  size?: UDModalSize;

  disableEscapeKey?: boolean;
  disableOverlayClick?: boolean;
}

/**
 * Каноническое окно подтверждения: заголовок по центру, «Отмена» (secondary) слева,
 * действие справа, обе на пол-ширины.
 *
 * Тон меняет ровно две вещи: вариант кнопки действия (`danger` / `primary`) и вид пояснения
 * (блок предупреждения / серый подзаголовок). Раскладка и порядок кнопок у тонов общие.
 * Слота для произвольного тела нет намеренно: предупреждение рисует кит, а не экран.
 *
 * Окно НЕ закрывает себя после `onConfirm` — закрывает вызывающая сторона,
 * когда операция действительно удалась. Иначе окно исчезало бы и при ошибке:
 * обработчики удаления ловят ошибку сами и возвращают выполненный промис.
 */
const UDConfirmModal: React.FC<UDConfirmModalProps> = ({
  show,
  title,
  description,
  confirmText,
  cancelText,
  tone = 'danger',
  isLoading,
  onConfirm,
  onCancel,
  size = 'sm',
  disableEscapeKey,
  disableOverlayClick,
}) => {
  const { t } = useLingui();
  const handleConfirm = useCallback(async () => {
    await onConfirm();
  }, [onConfirm]);

  const isDanger = tone === 'danger';

  return (
    <UDModal show={show} onClose={onCancel} size={size} disableEscapeKey={disableEscapeKey} disableOverlayClick={disableOverlayClick}>
      <ModalHeader title={title} subtitle={isDanger ? undefined : description} />
      {isDanger && description && (
        <div
          data-testid="confirm-warning"
          className="mt-4 flex items-start gap-2 rounded-card bg-CaseErrorSurface p-3 text-CaseErrorText"
        >
          {/* Иконка — в высоту первой строки (14px × 1.35 у relaxed): так она стоит вровень и с одной строкой, и с абзацем. */}
          <TriangleAlert size={18} className="shrink-0" aria-hidden />
          <UDText variant="text-2" relaxed className="whitespace-pre-wrap text-CaseErrorText">
            {description}
          </UDText>
        </div>
      )}
      <ModalFooter>
        <UDButton variant="secondary" onClick={onCancel} disabled={isLoading} stretch>
          {cancelText ?? t`Отмена`}
        </UDButton>
        <UDButton variant={isDanger ? 'danger' : 'primary'} onClick={handleConfirm} loading={Boolean(isLoading)} stretch>
          {confirmText ?? t`Удалить`}
        </UDButton>
      </ModalFooter>
    </UDModal>
  );
};

export default UDConfirmModal;
